import createTheme from '@material-ui/core/styles/createTheme'
import deepmerge from 'deepmerge'
import { rem } from './utils/rem'

export const theme = {
  ...createTheme({
    spacing: factor => rem(factor * 10),
    typography: {
      fontFamily: 'Apex Rounded, sans-serif',
      fontSize: 17,
      htmlFontSize: 17,
      body1: {
        fontSize: '1.25rem',
        color: '#1c1f2a',
        fontWeight: 'normal',
      },
      body2: {
        fontSize: '1rem',
        color: '#1c1f2a',
        fontWeight: 400,
        lineHeight: '2.05rem',
      },
      h1: {
        fontSize: '2.3rem',
      },
      h2: {
        fontSize: '2.3rem',
      },
      h3: {
        fontSize: '1.8rem',
      },
      h4: {
        fontSize: '1.6rem',
      },
      h5: {
        fontSize: '1.12rem',
        textTransform: 'uppercase',
      },
    },
    palette: {
      text: {
        primary: '#1c1f2a',
        secondary: '#4d4d4d',
        disabled: 'rgba(0, 0, 0, 0.2)',
      },
      primary: {
        main: '#f35a21',
        dark: '#1c1f2a',
        light: '#517F34',
      },
      secondary: {
        main: '#ed8b00',
        light: '#ffb81c',
        dark: '#D5420C',
      },
      common: {
        white: '#fff',
        black: '#000000',
      },
      background: {
        default: '#fff',
      },
    },
    props: {
      MuiButtonBase: {
        disableRipple: true,
      },
    },
  }),
  custom: {
    spacing: {
      paragraph: '2.5em',
    },
    colors: {
      lightSecondary: '#fbece7',
      grey: '#f2f2f2',
      darkRed: '#cb3701',
      orange: '#fff7f5',
      orangeHover: '#fff0ed',
      orangeTransparent: 'rgba(255, 120, 100, 0.1)',
      green: '#F2FFF4',
      lightOrange: '#fff7f5',
    },
  },
}

theme.custom = deepmerge(theme.custom, {
  spacing: {
    paragraph: rem(34, theme),
  },
  colors: {
    lightSecondary: '#fbece7',
    grey: '#f2f2f2',
    darkRed: '#cb3701',
    orange: '#fff7f5',
    orangeHover: '#fff0ed',
    orangeTransparent: 'rgba(255, 120, 100, 0.1)',
    green: '#F2FFF4',
    lightOrange: '#fff7f5',
  },
})

theme.typography.pxToRem = (px: number) => rem(px, theme)

theme.overrides = {
  MuiContainer: {
    root: {
      paddingLeft: rem(25),
      paddingRight: rem(25),

      [theme.breakpoints.up('sm')]: {
        paddingLeft: rem(25),
        paddingRight: rem(25),
      },
    },
    maxWidthXs: {
      [theme.breakpoints.up('xs')]: {
        maxWidth: rem(444 + (25 * 2)),
      },
    },
    maxWidthSm: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: rem(600 + (25 * 2)),
      },
    },
    maxWidthMd: {
      [theme.breakpoints.up('md')]: {
        maxWidth: rem(960 + (25 * 2)),
      },
    },
    maxWidthLg: {
      [theme.breakpoints.up('lg')]: {
        maxWidth: rem(1250 + (25 * 2)),
      },
    },
    maxWidthXl: {
      [theme.breakpoints.up('lg')]: {
        maxWidth: rem(1348 + (25 * 2)),
      },
    },
  },
  MuiTypography: {
    h1: {
      [theme.breakpoints.up('xs')]: {
        fontSize: theme.typography.pxToRem(30),
      },
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(38),
      },
    },
    h2: {
      color: '#D5420C',
      fontWeight: 600,
      lineHeight: theme.typography.pxToRem(30),
      marginTop: theme.typography.pxToRem(38 * 1.2),
      marginBottom: theme.typography.pxToRem(35),
      [theme.breakpoints.up('xs')]: {
        fontSize: theme.typography.pxToRem(22),
      },
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(32),
        lineHeight: theme.typography.pxToRem(44),
        marginBottom: theme.typography.pxToRem(42),
      },
    },
    h3: {
      fontWeight: 600,
      color: '#D5420C',
      marginTop: '1.5rem',
      marginBottom: '1em',
      lineHeight: 1.6,
      [theme.breakpoints.up('xs')]: {
        fontSize: theme.typography.pxToRem(22),
        lineHeight: theme.typography.pxToRem(30),
      },
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(25),
        lineHeight: theme.typography.pxToRem(36),
      },
    },
    h4: {
      fontWeight: 500,
      color: '#1C1F2A',
      marginTop: '1.5em',
      marginBottom: '1.3em',
      [theme.breakpoints.up('xs')]: {
        fontSize: theme.typography.pxToRem(18),
      },
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(22),
        lineHeight: theme.typography.pxToRem(35),
      },
    },
    h5: {
      color: '#1C1F2A',
      letterSpacing: 1.7,
      marginTop: theme.typography.pxToRem(15),
      marginBottom: theme.typography.pxToRem(10),
      [theme.breakpoints.up('xs')]: {
        fontSize: '14px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '14px',
      },
    },
    h6: {
      color: '#4d4d4d',
      marginBottom: '1.8em',
      fontSize: theme.typography.pxToRem(20),
      lineHeight: theme.typography.pxToRem(30),
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(22),
        lineHeight: theme.typography.pxToRem(35),
      },
    },
    body1:{
      [theme.breakpoints.up('xs')]: {
        fontSize: theme.typography.pxToRem(17),
      },
    },
    body2:{
      fontSize: theme.typography.pxToRem(15),
      lineHeight: theme.typography.pxToRem(28),
      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(17),
        lineHeight: theme.typography.pxToRem(35),
      },
    },
  },
  MuiButton: {
    root: {
      textTransform: 'none',
      lineHeight: 1,
      margin: undefined,
    },
    outlined: {
      paddingTop: theme.typography.pxToRem(12),
      paddingRight:theme.typography.pxToRem(10),
      paddingBottom: theme.typography.pxToRem(11),
      paddingLeft: theme.typography.pxToRem(14),
      color: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      borderWidth: '1px',
    },
    outlinedPrimary: {
      paddingTop: theme.typography.pxToRem(12),
      paddingRight:theme.typography.pxToRem(10),
      paddingBottom: theme.typography.pxToRem(11),
      paddingLeft: theme.typography.pxToRem(14),
      color: theme.palette.secondary.dark,
      borderColor: theme.palette.secondary.dark,
      borderWidth: '1px',
      backgroundColor: theme.custom.colors.orangeTransparent,
      '&:hover': {
        backgroundColor: theme.custom.colors.orangeTransparent,
        border: '1px solid',
        borderColor: theme.palette.secondary.dark,
      },
    },
    outlinedSecondary: {
      paddingTop: theme.typography.pxToRem(12),
      paddingRight:theme.typography.pxToRem(10),
      paddingBottom: theme.typography.pxToRem(11),
      paddingLeft: theme.typography.pxToRem(14),
      color: theme.palette.primary.light,
      borderColor: theme.palette.primary.light,
      borderWidth:'1px',
      backgroundColor: theme.custom.colors.green,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
        border: '1px solid',
        borderColor: theme.palette.primary.light,
        color: 'white',
      },
    },
    outlinedSizeSmall : {
      fontSize: theme.typography.pxToRem(16),
      padding: `${theme.typography.pxToRem(12)} ${theme.typography.pxToRem(16)} ${theme.typography.pxToRem(11)}`,
      borderWidth:'1px',
    },
    containedPrimary: {
      fontSize: theme.typography.pxToRem(16),
      padding:`${theme.typography.pxToRem(15)} ${theme.typography.pxToRem(25)}`,
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      borderRadius: '8px',
      boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.1)',
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(25),
        padding: `${theme.typography.pxToRem(22)} ${theme.typography.pxToRem(35)}`,
      },
    },
    containedSecondary : {
      padding: '14px 20px',
      backgroundColor: 'transparent',
      border: `solid 1px ${theme.palette.secondary.dark}`,
      color: theme.palette.common.white,
      borderWidth: '1px',
      [theme.breakpoints.up('md')]: {
        padding: '12px 25px',
      },
    },
    containedSizeSmall: {
      padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(9)}`,
    },
    textPrimary: {
      fontSize: theme.typography.pxToRem(15),
      color: theme.palette.text.primary,
      textDecoration: 'underline',
      transition: 'color 0.1s ease-in-out',
      border: 'none',
      '&:hover': {
        color: theme.custom.colors.darkRed,
        backgroundColor: 'transparent',
      },
    },
    endIcon: {
      marginLeft: '2px',
    },
  },
  MuiTableCell: {
    root: {
      paddingTop: theme.typography.pxToRem(20),
      paddingRight: theme.typography.pxToRem(27),
      paddingBottom: theme.typography.pxToRem(16),
      paddingLeft: theme.typography.pxToRem(16),
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.6,

      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(17),
        paddingTop: theme.typography.pxToRem(25),
        paddingRight: theme.typography.pxToRem(40),
        paddingLeft: theme.typography.pxToRem(30),
      },
    },
    head: {
      backgroundColor: theme.custom.colors.orange,
      borderBottom: '2px solid rgba(213, 66, 12, 0.6)',
      color: theme.custom.colors.darkRed,
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',

      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(16),
        paddingTop: theme.typography.pxToRem(16),
      },
    },
  },
}

export type ThemeType = typeof theme
